import {type CSSProperties, useEffect, useId, useRef} from 'react'

import ditherFallbackMask from '../../assets/images/fallbacks/dither/dithering.webp'
import ditherFallbackPearl from '../../assets/images/fallbacks/dither/pearl.webp'
import {type DitherParams, useWebGLContext, useWebGLStatus} from '../WebGLContext/WebGLContext'

import styles from './WebGLDither.module.css'

interface WebGLDitherProps {
  className?: string
  params: DitherParams
}

const FALLBACK_BACKGROUNDS: Record<DitherParams['color'], CSSProperties> = {
  default: {background: 'linear-gradient(0deg, #08872b 0%, #ffffff 120%)'},
  copilot: {background: 'linear-gradient(0deg, #8b34fc 0%, #ffffff 120%)'},
  security: {background: 'linear-gradient(0deg, #006edb 0%, #ffffff 120%)'},
  pearl: {
    backgroundColor: '#becdf8',
    backgroundImage: `url("${ditherFallbackPearl}")`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  },
}

const FALLBACK_DOT_COLORS: Record<DitherParams['color'], string> = {
  default: '#8dd6ff',
  copilot: '#501daf',
  security: '#bfffd1',
  pearl: '#8dd6ff',
}

export function WebGLDither({className, params}: WebGLDitherProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isRegisteredRef = useRef(false)
  const instanceRef = useRef<number>(0)

  const {registerDither, unregisterDither} = useWebGLContext()
  const webGLStatus = useWebGLStatus()
  const id = useId()
  const fallbackStyle = webGLStatus === 'unavailable' ? FALLBACK_BACKGROUNDS[params.color] : undefined

  const getInstanceId = () => `${id}-${instanceRef.current}`

  useEffect(() => {
    if (!ref.current) return
    if (isRegisteredRef.current) {
      unregisterDither(getInstanceId())
      isRegisteredRef.current = false
    }

    instanceRef.current++

    registerDither({id: getInstanceId(), element: ref.current, params})
    isRegisteredRef.current = true
    // Re-registration is deliberately keyed on the individual `params` fields:
    // the `params` object itself is a new literal every render, and
    // `getInstanceId` is redefined every render, so listing either as a dep
    // would re-register the dither on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.color, params.type, params.fadeIn, params.dotOpacity])

  useEffect(() => {
    return () => {
      if (isRegisteredRef.current) {
        unregisterDither(getInstanceId())
        isRegisteredRef.current = false
      }
    }
    // Unmount-only cleanup. `getInstanceId` is redefined every render, so
    // listing it would run this cleanup — unregistering the dither — on every
    // render. It only reads `id` and a ref, so the stale closure is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div ref={ref} className={className} data-webgl-status={webGLStatus} style={fallbackStyle}>
      {webGLStatus === 'unavailable' && (
        <div
          className={styles.Fallback}
          style={{
            backgroundColor: FALLBACK_DOT_COLORS[params.color],
            maskImage: `url("${ditherFallbackMask}")`,
            WebkitMaskImage: `url("${ditherFallbackMask}")`,
            opacity: params.dotOpacity ?? (params.color === 'pearl' ? 0.2 : 0.5),
          }}
          aria-hidden="true"
        />
      )}
    </div>
  )
}
